import React from 'react';
import {
	PrimaryButtonCompat as PrimaryButton,
	StackCompat as Stack,
} from 'components/FluentUICompat';
import {useUserContext} from 'authentication/UserContext';
import {Control} from 'react-hook-form';
import {useGetRequirementsFormDataQuery} from '../hooks/requirements.generated';
import {FormMode} from 'components/EntityPage/EntityPage.types';
import {ListOrDetailsRequirement} from '../requirements.types';
import {
	CommonFormElementsOfRequirements,
	DataForSelectableFields,
	createDataForSelectableFields,
} from './CommonFormElementsOfRequirements';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';

type RequirementFormElementProps<Requirement extends ListOrDetailsRequirement> =
	{
		control: Control<Requirement>;
		mode: FormMode;
		id: string;
		onAggregateClick?: () => void;
	};

export const RequirementFormElements = <
	Requirement extends ListOrDetailsRequirement,
>({
	control,
	mode,
	onAggregateClick,
	id,
}: RequirementFormElementProps<Requirement>): React.ReactElement => {
	const {t} = useRequirementsTranslation();
	const {myVexClusters, isAdmin} = useUserContext();

	const {data: formData} = useGetRequirementsFormDataQuery({
		skip: mode === FormMode.None,
	});

	const vexClusters = React.useMemo(() => {
		const {vexClusterData}: DataForSelectableFields =
			createDataForSelectableFields(formData);
		return isAdmin ? vexClusterData : myVexClusters;
	}, [isAdmin, myVexClusters, formData]);

	const renderPossibleAggregationBtn = (): React.ReactElement | null => {
		if (mode !== FormMode.Update) return null;
		return (
			<PrimaryButton
				styles={{root: {marginTop: 10}}}
				onClick={onAggregateClick}
			>
				{t('ManualAggregation')}
			</PrimaryButton>
		);
	};

	const disableRule = React.useCallback(
		(fieldName: string): boolean =>
			fieldName === 'vexClusters' &&
			control._formValues.status === 'FINAL' &&
			control._formValues.vexClusters?.length > 0,
		[control._formValues.status, control._formValues.vexClusters?.length],
	);

	return (
		<Stack>
			<CommonFormElementsOfRequirements
				control={control}
				formData={formData}
				selectableVexClusters={vexClusters}
				aggregationBtn={renderPossibleAggregationBtn()}
				id={id}
				disableRule={disableRule}
			/>
		</Stack>
	);
};
